import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import styles from "./QuestionBodyMemoryMatch.module.css";
import PopUpMenu from "../PopUpMenu/PopUpMenu";
import { activityLogSubmission } from "../../../helper";
import axios from "axios";
import { baseUrl, dataContext } from "../../../App";
import { useNavigate } from "react-router-dom";

// Map any incoming difficulty value (including aliases like easy/medium/hard)
// to a canonical key that matches difficultySettings and the CSS grid classes.
const DIFFICULTY_ALIASES = {
  beginner: "beginner",
  intermediate: "intermediate",
  advanced: "advanced",
  easy: "beginner",
  medium: "intermediate",
  hard: "advanced",
};
const normalizeDifficulty = (value) =>
  DIFFICULTY_ALIASES[value] || "beginner";

const QuestionBodyMemoryMatch = ({ initialDifficulty = "beginner",playerRound }) => {
  // Game state
  const {token, playerData} = useContext(dataContext);
  const navigate = useNavigate();
  const [gameState, setGameState] = useState({
    cards: [],
    flippedCards: [],
    matchedPairs: 0,
    moves: 0,
    startTime: null,
    isPaused: false,
    difficulty: normalizeDifficulty(initialDifficulty),
    timerInterval: null,
    elapsedTime: 0,
    // totalTime: 180, // Removed time limit - now unlimited
  });

  const [showModal, setShowModal] = useState(false);
  const pauseTimeoutRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const [showPopUp, setShowPopUp] = useState(false);

  const totalCardClicks = useRef(0); // every card flip the player actually spent
  const totalSolvedCards = useRef(0); // cards cleared: 2 per matched pair
  const pairsSolved = useRef(0);
  const initialRecordSent = useRef(false);

  // Card symbols - using memory-icons image paths
  const allCardSymbols = [
    require("../../../assets/memory-icons/1.png"),
    require("../../../assets/memory-icons/2.png"),
    require("../../../assets/memory-icons/3.png"),
    require("../../../assets/memory-icons/4.png"),
    require("../../../assets/memory-icons/5.png"),
    require("../../../assets/memory-icons/6.png"),
    require("../../../assets/memory-icons/7.png"),
    require("../../../assets/memory-icons/8.png"),
    require("../../../assets/memory-icons/9.png"),
    require("../../../assets/memory-icons/10.png"),
    require("../../../assets/memory-icons/11.png"),
    require("../../../assets/memory-icons/12.png"),
    require("../../../assets/memory-icons/13.png"),
    require("../../../assets/memory-icons/14.png"),
    require("../../../assets/memory-icons/15.png"),
    require("../../../assets/memory-icons/16.png"),
  ];

  function submitProgressRecord() {
    let formatDifficulty = gameState.difficulty==='beginner'?'easy':gameState.difficulty==='intermediate'?'medium':'hard';
    axios.post(`${baseUrl}/api/participant/quizplay_record`, {
      msisdn: playerData?.msisdn,
      category: formatDifficulty,
      time_taken: gameState.elapsedTime,
      round_number: playerRound + 1,
      event: 151,
      right_count: pairsSolved.current,
      question_count: 1,
      serviceType: "MemoryMatch",
      portal: 18,
      round_questions: `${totalSolvedCards.current}_${totalCardClicks.current}`
    }, {
      headers: { Authorization: `Token ${token}` }
    });
  }

  function handleMemoryMatchResultSubmission(totalTime,difficultyLevel) {
    let formatDifficulty = difficultyLevel==='beginner'?'easy':difficultyLevel==='intermediate'?'medium':'hard';
    axios.post(`${baseUrl}/api/participant/memorymatch`, {
      msisdn: playerData?.msisdn,
      total_time: totalTime,
      round_number: playerRound + 1,
      difficulty_level:formatDifficulty,
      event: 151,
      portal: 18,
      round_questions: `${totalSolvedCards.current}_${totalCardClicks.current}`
    }, {
      headers: { Authorization: `Token ${token}` }
    });
}
function getServiceTypeFromEventId(eventId) {
  if (eventId === 34) {
    return 'Daily'
  } else if (eventId === 75) {
    return 'Sports'
  } else if (eventId === 149) {
    return 'BCS'
  } else if (eventId === 150) {
    return 'Tournament'
  } else if (eventId === 151) {
    return 'MemoryMatch'
  } else if (eventId === 134) {
    return 'SpellingBee'
  } else if (eventId === 67 || eventId === 79) {
    return 'Wordly'
  }
}
function submitFinalScore(){
  let difficultyLevel = gameState.difficulty;
    let formatDifficulty = difficultyLevel==='beginner'?'easy':difficultyLevel==='intermediate'?'medium':'hard';

  axios.post(`${baseUrl}/api/participant/quizplay_summery_submission`, {
    // user: playerData?.id,
    msisdn: playerData?.msisdn,
    category: formatDifficulty,
    time_taken: Math.min(Math.floor(gameState.elapsedTime), 1000),
    round_number: playerRound + 1,
    event: 151,
    right_count: 1,
    question_count: 1,
    serviceType: "MemoryMatch",
    portal: 18,
    round_questions: `${totalSolvedCards.current}_${totalCardClicks.current}`
  }, {
    headers: { Authorization: `Token ${token}` }
  });
}

  // Difficulty settings
  const difficultySettings = {
    beginner: { pairs: 8, columns: 4 },
    intermediate: { pairs: 12, columns: 6 },
    advanced: { pairs: 16, columns: 8 },
  };

  // Start timer
  const startTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    timerIntervalRef.current = setInterval(() => {
      setGameState((prevState) => {
        if (!prevState.isPaused) {
          return {
            ...prevState,
            elapsedTime: Math.floor((Date.now() - prevState.startTime) / 1000),
          };
        }
        return prevState;
      });
    }, 1000);
  }, []);

  // Format time display in Bangla
  const formatTimeBangla = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    const banglaNumbers = ["০", "১", "২", "৩", "৪", "৫", "৬", "৭", "৮", "৯"];

    const toBangla = (num) => {
      return num
        .toString()
        .split("")
        .map((digit) => banglaNumbers[parseInt(digit)])
        .join("");
    };

    return `${toBangla(minutes)}:${toBangla(secs.toString().padStart(2, "0"))}`;
  };
  function formatNumberBangla(number) {
    const banglaNumbers = ["০", "১", "২", "৩", "৪", "৫", "৬", "৭", "৮", "৯"];
    return number.toString().split("").map((digit) => banglaNumbers[parseInt(digit)]).join("");
  }

  // Handle pause timeout (2 minutes)
  const handlePauseTimeout = useCallback(() => {
    window.location.reload();
  }, []);

  // Start new game
  const startNewGame = useCallback(() => {
    const settings =
      difficultySettings[gameState.difficulty] || difficultySettings.beginner;

    // Clear existing timers
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }

    // Randomly select unique icons for the pairs
    const shuffledIcons = [...allCardSymbols];
    for (let i = shuffledIcons.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledIcons[i], shuffledIcons[j]] = [shuffledIcons[j], shuffledIcons[i]];
    }
    const symbols = shuffledIcons.slice(0, settings.pairs);
    const cardPairs = [...symbols, ...symbols];

    // Shuffle cards
    for (let i = cardPairs.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [cardPairs[i], cardPairs[j]] = [cardPairs[j], cardPairs[i]];
    }

    // Create card objects
    const cards = cardPairs.map((symbol, index) => ({
      id: index,
      symbol: symbol,
      isFlipped: false,
      isMatched: false,
    }));

    setGameState((prevState) => ({
      ...prevState,
      cards,
      flippedCards: [],
      matchedPairs: 0,
      moves: 0,
      startTime: Date.now(),
      isPaused: false,
      elapsedTime: 0,
    }));

    setShowModal(false);
    startTimer();
  }, [gameState.difficulty, startTimer]);

  // Handle card click
  const handleCardClick = useCallback(
    (index) => {
      if (
        gameState.isPaused ||
        gameState.cards[index]?.isMatched ||
        gameState.cards[index]?.isFlipped ||
        gameState.flippedCards.length === 2
      ) {
        return;
      }
      totalCardClicks.current += 1;

      setGameState((prevState) => {
        const newCards = [...prevState.cards];
        newCards[index].isFlipped = true;
        const newFlippedCards = [...prevState.flippedCards, index];

        return {
          ...prevState,
          cards: newCards,
          flippedCards: newFlippedCards,
        };
      });
    },
    [gameState.isPaused, gameState.cards, gameState.flippedCards]
  );

  // Check for matches
  useEffect(() => {
    if (gameState.flippedCards.length === 2) {
      const [first, second] = gameState.flippedCards;
      const firstCard = gameState.cards[first];
      const secondCard = gameState.cards[second];

      setTimeout(() => {
        if (firstCard.symbol === secondCard.symbol) {
          totalSolvedCards.current += 2;
          pairsSolved.current += 1;
          if (pairsSolved.current % 4 === 0) submitProgressRecord();
        }

        setGameState((prevState) => {
          const newCards = [...prevState.cards];
          let newMatchedPairs = prevState.matchedPairs;

          if (firstCard.symbol === secondCard.symbol) {
            // Match found
            newCards[first].isMatched = true;
            newCards[second].isMatched = true;
            newMatchedPairs++;
          } else {
            // No match - flip cards back
            newCards[first].isFlipped = false;
            newCards[second].isFlipped = false;
          }

          const newMoves = prevState.moves + 1;

          return {
            ...prevState,
            cards: newCards,
            flippedCards: [],
            matchedPairs: newMatchedPairs,
            moves: newMoves,
          };
        });
      }, 800);
    }
  }, [gameState.flippedCards, gameState.cards]);

  // Check if game is complete
  useEffect(() => {
    const settings =
      difficultySettings[gameState.difficulty] || difficultySettings.beginner;
    if (
      gameState.matchedPairs === settings.pairs &&
      gameState.matchedPairs > 0
    ) {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      handleMemoryMatchResultSubmission(gameState.elapsedTime,gameState.difficulty)
      submitFinalScore()
      setShowModal(true);
    }
  }, [gameState.matchedPairs, gameState.difficulty]);

  // Handle pause/resume
  const togglePause = useCallback(() => {
    if (gameState.isPaused) {
      setShowPopUp(true);
    }
    setGameState((prevState) => {
      const newIsPaused = !prevState.isPaused;

      if (newIsPaused) {
        // Game is being paused - set 2 minute timeout
        pauseTimeoutRef.current = setTimeout(handlePauseTimeout, 2 * 60 * 1000);
      } else {
        // Game is being resumed - clear timeout
        if (pauseTimeoutRef.current) {
          clearTimeout(pauseTimeoutRef.current);
          pauseTimeoutRef.current = null;
        }
        // Update start time to account for pause duration
        const pauseDuration =
          Date.now() - prevState.startTime - prevState.elapsedTime * 1000;
        return {
          ...prevState,
          isPaused: newIsPaused,
          startTime: Date.now() - prevState.elapsedTime * 1000,
        };
      }

      return {
        ...prevState,
        isPaused: newIsPaused,
      };
    });
  }, [handlePauseTimeout]);

  // Handle difficulty change
  const changeDifficulty = useCallback((newDifficulty) => {
    setGameState((prevState) => ({
      ...prevState,
      difficulty: newDifficulty,
    }));
  }, []);

  // Initialize game on mount
  useEffect(() => {
    startNewGame();
    if (!initialRecordSent.current) {
      initialRecordSent.current = true;
      submitProgressRecord();
    }

    // Cleanup on unmount
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, []);

  // Update game when initialDifficulty changes
  useEffect(() => {
    const normalized = normalizeDifficulty(initialDifficulty);
    if (normalized !== gameState.difficulty) {
      setGameState((prevState) => ({
        ...prevState,
        difficulty: normalized,
      }));
    }
  }, [initialDifficulty]);

  // Update game when difficulty changes
  useEffect(() => {
    startNewGame();
  }, [gameState.difficulty]);

  return (
    <div className={styles.gameContainer}>
      {showPopUp && <PopUpMenu setShowPopUp={setShowPopUp} />}
      {/* Header with Logo */}
      <div className={styles.logoContainer}>
        <img src={"/logo/wordly_logo.png"} alt="logo" className={styles.logo} />
      </div>
        <div style={{width: '100%', textAlign: 'center', fontSize: '20px', fontWeight: 'bold', color: '#fff',display:'flex',justifyContent:'center',alignItems:'center',gap:'16px',position:'relative'}}>
          মেমোরি ম্যাচ
          <img src={require('./box.png')} alt="" style={{ position:'absolute',right:'0px',height:'100%',cursor:'pointer' }} onClick={() => setShowPopUp(true)} />
        </div>


      {/* Game Stats */}
      <div className={styles.statsContainer} style={{margin:'16px 0px'}}>
        <div className={styles.statItem}>
          <div className={styles.statLabel}>স্কোর:</div>
          <div className={styles.statValue}>{formatNumberBangla(gameState.matchedPairs)}</div>
        </div>
        <div className={styles.statItem}>
          <div className={styles.statLabel}>মুভ:</div>
          <div className={styles.statValue}>{formatNumberBangla(gameState.moves)}</div>
        </div>
        <div className={styles.statItem} style={{width: '80px'}}>
          <div className={styles.statLabel}>সময়:</div>
          <div className={styles.statValue}>
            {formatTimeBangla(gameState.elapsedTime)}
          </div>
        </div>
      </div>


      {/* Game Board */}
      <div className={styles.gameBoard}>
        <div className={`${styles.cardsGrid} ${styles[gameState.difficulty]}`}>
          {gameState.cards.map((card, index) => (
            <div
              key={card.id}
              className={`${styles.card} ${
                card.isFlipped || card.isMatched ? styles.flipped : ""
              } ${card.isMatched ? styles.matched : ""}`}
              onClick={() => handleCardClick(index)}
            >
              <div className={`${styles.cardFace} ${styles.cardFront}`}>?</div>
              <div className={`${styles.cardFace} ${styles.cardBack}`}>
                {card.symbol && (
                  <img src={card.symbol} alt="icon" style={{ width: '80%', height: '80%' }} />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>



      {/* Game Complete Modal */}
      {showModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h2 className={styles.modalTitle}>🎉 অভিনন্দন!</h2>
            <div className={styles.modalText}>
              আপনি গেমটি সম্পূর্ণ করেছেন {formatNumberBangla(gameState.moves)} মুভে এবং{" "}
              {formatTimeBangla(gameState.elapsedTime)} সময়ে!
            </div>

            <div className={styles.modalControls}>
              <button className={styles.gameBtn} onClick={()=>{navigate(`../?page=leader&eventId=151`)}}>
                লিডারবোর্ড
              </button>
              <button
                className={`${styles.gameBtn} ${styles.secondaryBtn}`}
                onClick={()=>{navigate(`../?page=home&eventId=151`)}}
              >
                হোম
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionBodyMemoryMatch;
